import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PropertyHeader from './components/PropertyHeader';
import QuickVerdict from './components/QuickVerdict';
import StrengthsSection from './components/StrengthsSection';
import RisksSection from './components/RisksSection';
import OfferBandSection from './components/OfferBandSection';
import NextStepsSection from './components/NextStepsSection';
import { AnalysisDetailSkeleton, AnalysisEmptyState } from './components/AnalysisDetailSkeleton';
import { generateAnalysisPDF } from '../../utils/generateAnalysisPDF';
import MarketTrendSummary from '../../components/ui/MarketTrendSummary';
import { useLanguage } from '../../contexts/LanguageContext';

const fallbackAnalysis = {
  id: "ANL-2026-001",
  property: {
    address: "42 Harbour View Terrace",
    suburb: "Mosman",
    state: "NSW",
    postcode: "2088",
    bedrooms: 4,
    bathrooms: 3,
    parking: 2,
    pricingGuide: "$2,800,000 - $3,100,000",
    listingUrl: "https://www.domain.com.au/42-harbour-view-terrace-mosman-nsw-2088",
    image: "https://images.unsplash.com/photo-1723152928591-da032d949cff",
    imageAlt: "Modern two-story luxury home with white facade, large windows, manicured front garden with palm trees and stone pathway leading to entrance"
  },
  verdict: {
    type: "fair",
    summary: "This Mosman property appears fairly priced for the current market conditions. The location in a premium harbour-side suburb justifies the asking range, and recent comparable sales support the vendor's expectations.\n\nThe property offers excellent value with its north-facing aspect, recent renovations, and proximity to Balmoral Beach. While the market has softened slightly in recent months, this type of family home in Mosman continues to attract strong buyer interest.\n\nThe pricing guide aligns well with recent sales of similar 4-bedroom homes in the area, with properties selling between $2.7M and $3.2M in the past 6 months."
  },
  strengths: [
  "Premium location in highly sought-after Mosman with excellent school catchment",
  "North-facing rear garden with harbour glimpses, maximizing natural light",
  "Recently renovated kitchen and bathrooms (2024) with high-quality fixtures",
  "Walking distance to Balmoral Beach (850m), local shops, cafes, and transport",
  "Solid brick construction with good bones, offering potential for future value-add"],

  risks: [
  "Property is on a busy road which may impact noise levels and resale appeal",
  "No recent building inspection available - recommend comprehensive pre-purchase inspection",
  "Older electrical wiring may require updating to meet current safety standards",
  "Limited information about drainage and potential water ingress issues",
  "Days on market (47 days) suggests vendor may have initially overpriced"],

  offerBand: { min: 2650000, max: 2900000 },
  questionsForAgent: [
  "What is the vendor's timeline for selling?",
  "Can you provide copies of recent building and pest inspection reports?",
  "What comparable sales are you using to justify the current pricing guide?",
  "Has there been any water damage or drainage issues in the property\'s history?",
  "Are there any planned developments in the immediate area?"],

  nextSteps: [
  "Arrange a comprehensive building and pest inspection within 7 days",
  "Obtain a strata report to review financial health of the building",
  "Research recent comparable sales in the area within 1km radius",
  "Visit the property at different times of day to assess traffic and noise",
  "Arrange pre-approval for finance to strengthen your negotiating position"],

  confidence: { overall: 87, verdict: 89, strengths: 92, risks: 85, offerBand: 84 },
  analysisDate: "2026-02-20T14:30:00",
  status: "complete"
};

function buildDisplayAnalysis(rawAnalysis) {
  if (!rawAnalysis) return fallbackAnalysis;

  const prop = rawAnalysis?.property || {};
  const addressParts = (prop?.address || '')?.split(',');
  const streetAddress = addressParts?.[0]?.trim() || prop?.address || 'Property Analysis';
  const suburbPart = addressParts?.[1]?.trim() || prop?.region || '';

  return {
    id: rawAnalysis?.id || `ANL-${Date.now()}`,
    property: {
      address: streetAddress,
      suburb: suburbPart,
      state: prop?.state || '',
      postcode: prop?.postcode || '',
      bedrooms: prop?.bedrooms ? parseInt(prop?.bedrooms) : undefined,
      bathrooms: prop?.bathrooms ? parseInt(prop?.bathrooms) : undefined,
      parking: prop?.parking,
      pricingGuide: prop?.price || 'Price on application',
      listingUrl: prop?.listingUrl || '',
      image: prop?.image || 'https://images.unsplash.com/photo-1723152928591-da032d949cff',
      imageAlt: prop?.imageAlt || 'Property exterior view'
    },
    verdict: rawAnalysis?.verdict || fallbackAnalysis?.verdict,
    strengths: rawAnalysis?.strengths || fallbackAnalysis?.strengths,
    risks: rawAnalysis?.risks || fallbackAnalysis?.risks,
    offerBand: rawAnalysis?.offerBand || fallbackAnalysis?.offerBand,
    questionsForAgent: rawAnalysis?.questionsForAgent || fallbackAnalysis?.questionsForAgent,
    nextSteps: rawAnalysis?.nextSteps || fallbackAnalysis?.nextSteps,
    confidence: rawAnalysis?.confidence || fallbackAnalysis?.confidence,
    analysisDate: rawAnalysis?.analysisDate || new Date()?.toISOString(),
    status: rawAnalysis?.status || 'complete'
  };
}

const AnalysisDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [user] = useState({
    name: "Sarah Mitchell",
    email: "sarah.mitchell@email.com",
    role: "user"
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const rawAnalysis = location?.state?.analysis || null;
  const analysis = buildDisplayAnalysis(rawAnalysis);
  const isAIGenerated = !!rawAnalysis;

  useEffect(() => {
    window.scrollTo(0, 0);
    // Simulate brief loading for skeleton display
    const timer = setTimeout(() => setIsLoading(false), 900);
    return () => clearTimeout(timer);
  }, []);

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleNewAnalysis = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    navigate('/authentication');
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      generateAnalysisPDF(analysis);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBackToDashboard}
              className="mb-4">
              {t('backToDashboard')}
            </Button>

            {!isLoading &&
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                      {t('propertyAnalysisReport')}
                    </h1>
                    {isAIGenerated &&
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                        <Icon name="Sparkles" size={12} color="currentColor" />
                        {t('aiGenerated')}
                      </span>
                  }
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={16} color="currentColor" />
                      <span className="text-caption">{t('analysisId')}: <span className="font-semibold text-data">{analysis?.id}</span></span>
                    </div>
                    <span className="text-muted-foreground">•</span>
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={16} color="currentColor" />
                      <span className="text-caption">
                        {new Date(analysis.analysisDate)?.toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                      </span>
                    </div>
                    <span className="text-muted-foreground">•</span>
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} color="currentColor" />
                      <span className="text-caption">
                        {new Date(analysis.analysisDate)?.toLocaleTimeString('en-AU', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleNewAnalysis}>
                  {t('newAnalysis')}
                </Button>
              </div>
            }
          </div>

          {isLoading ?
          <AnalysisDetailSkeleton /> :
          !analysis?.property?.address ?
          <AnalysisEmptyState onStartAnalysis={handleNewAnalysis} /> :

          <div className="space-y-6 md:space-y-8">
              <PropertyHeader property={analysis?.property} />

              <QuickVerdict
              verdict={analysis?.verdict}
              confidence={analysis?.confidence?.verdict} />

              <StrengthsSection
              strengths={analysis?.strengths}
              confidence={analysis?.confidence?.strengths} />

              <RisksSection
              risks={analysis?.risks}
              confidence={analysis?.confidence?.risks} />

              <OfferBandSection
              offerBand={analysis?.offerBand}
              confidence={analysis?.confidence?.offerBand} />

              <MarketTrendSummary
              suburb={analysis?.property?.suburb}
              state={analysis?.property?.state}
              city={analysis?.property?.suburb}
              propertyPrice={analysis?.offerBand?.min}
              propertyType="residential" />
            

              <NextStepsSection
              nextSteps={analysis?.nextSteps}
              questionsForAgent={analysis?.questionsForAgent} />

              <div className="flex flex-col sm:flex-row gap-4 pt-6 md:pt-8 border-t border-border">
                <Button
                variant="outline"
                iconName={isGeneratingPDF ? 'Loader2' : 'Download'}
                iconPosition="left"
                fullWidth
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="sm:flex-1">
                  {isGeneratingPDF ? t('generatingPDF') : t('downloadPDFReport')}
                </Button>
                <Button
                variant="outline"
                iconName="Share2"
                iconPosition="left"
                fullWidth
                className="sm:flex-1">
                  {t('shareAnalysis')}
                </Button>
                <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                fullWidth
                onClick={handleNewAnalysis}
                className="sm:flex-1">
                  {t('newAnalysis')}
                </Button>
              </div>
            </div>
          }
        </div>
      </main>
    </div>);

};

export default AnalysisDetail;